import { API } from '@/config';
import type {
  ApplicationLog,
  CrashGroup,
  CrashGroupQuery,
  CrashListQuery,
  CrashReport,
  DashboardQuery,
  DashboardResponse,
  LogListQuery,
} from '@/types';
import { api } from './client';
import { getPaginated, type Paginated } from './paginated';

/**
 * What the fleet is reporting: the dashboard, the log viewer, crash triage.
 *
 * A thin layer over the client, kept separate so components never build URLs or
 * know which endpoint does what.
 */
export const telemetryService = {
  /**
   * The opening screen.
   *
   * `windowHours` is capped server-side at a fortnight - beyond that the counts
   * stop describing now, and the server refuses rather than answering slowly.
   */
  dashboard: (query: DashboardQuery = {}, signal?: AbortSignal): Promise<DashboardResponse> =>
    api.get<DashboardResponse>(
      `${API.endpoints.dashboard}${query.windowHours !== undefined ? `?windowHours=${String(query.windowHours)}` : ''}`,
      signal ? { signal } : {},
    ),

  /**
   * The log viewer.
   *
   * Always sent with a limit. This is the largest table in the system and an
   * unfiltered read is a way to take the database down; the server caps it too,
   * but a client that asks for everything is a client that will eventually be
   * pointed at a server that forgot to.
   */
  logs: (query: LogListQuery, signal?: AbortSignal): Promise<Paginated<ApplicationLog>> =>
    getPaginated<ApplicationLog>(API.endpoints.logs, query, signal, 'Could not load logs.'),

  crashes: (query: CrashListQuery, signal?: AbortSignal): Promise<Paginated<CrashReport>> =>
    getPaginated<CrashReport>(API.endpoints.crashes, query, signal, 'Could not load crashes.'),

  /** Crashes grouped by signature over the last `days` days, most reports first. */
  crashGroups: (query: CrashGroupQuery, signal?: AbortSignal): Promise<CrashGroup[]> => {
    const params = new URLSearchParams();
    if (query.days !== undefined) params.set('days', String(query.days));
    if (query.appComponent) params.set('appComponent', query.appComponent);
    const search = params.toString();
    return api.get<CrashGroup[]>(
      `${API.endpoints.crashGroups}${search ? `?${search}` : ''}`,
      signal ? { signal } : {},
    );
  },
};
